import { useEffect, useState } from "react";
import { useFindMany } from "@gadgetinc/react";
import { api } from "../api";

/**
 * Reusable hook for semester filtering across views.
 * Fetches semesters for a given cohort and manages the selected semester state.
 *
 * @param {string} cohortId - The cohort to load semesters for
 * @returns {{ semesters, selectedSemesterId, setSelectedSemesterId, semesterDateRange, loading }}
 */
export function useSemesterFilter(cohortId) {
  const [selectedSemesterId, setSelectedSemesterId] = useState("");

  const [{ data: semesters, fetching: loading }] = useFindMany(api.cohortSemester, {
    filter: { cohortId: { equals: cohortId } },
    sort: { sortOrder: "Ascending" },
    select: {
      id: true,
      name: true,
      startDate: true,
      endDate: true,
      sortOrder: true,
    },
    pause: !cohortId,
  });

  // Reset selected semester when cohort changes
  useEffect(() => {
    setSelectedSemesterId("");
  }, [cohortId]);

  // Default semester: one containing today, otherwise first semester when only one exists
  useEffect(() => {
    if (!cohortId || loading || selectedSemesterId) return;
    if (!Array.isArray(semesters) || semesters.length === 0) return;

    const now = new Date();
    const containingToday = semesters.find((semester) => {
      const start = semester?.startDate ? new Date(semester.startDate) : null;
      const end = semester?.endDate ? new Date(semester.endDate) : null;
      if (!start || !end || Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return false;
      return now >= start && now <= end;
    });

    if (containingToday?.id) {
      setSelectedSemesterId(containingToday.id);
      return;
    }

    if (semesters.length === 1 && semesters[0]?.id) {
      setSelectedSemesterId(semesters[0].id);
    }
  }, [cohortId, semesters, loading, selectedSemesterId]);

  const selectedSemester = selectedSemesterId
    ? (semesters || []).find((s) => s.id === selectedSemesterId)
    : null;

  const semesterDateRange = selectedSemester
    ? { startDate: selectedSemester.startDate, endDate: selectedSemester.endDate }
    : null;

  return {
    semesters: semesters || [],
    selectedSemesterId,
    setSelectedSemesterId,
    semesterDateRange,
    loading,
  };
}
